import {
  coerceWhoWereReachingCounties,
  coerceWhoWereReachingDistribution,
  normalizeWhoWereReachingSnapshot,
  readReportFieldSource,
  REPORT_MOCK_FIELD_OPACITY,
} from '@act/shared/data-transfer-objects';
import { Fragment } from 'react';
import { motion } from "framer-motion";
import SectionReveal from "./SectionReveal";
import CountyEngagementMap, {
  type CountyEngagementRow,
} from "./CountyEngagementMap";
import { useStateCountyGeo } from "../geo/useStateCountyGeo.hook";
import { useReportsPrintStaticLayout } from "../hooks/useReportsPrintStaticLayout.hook";
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import { foreground, muted, border, secondary, body, CORAL, NAVY } from "./tokens";
import type { ReportHostedSectionProps } from '../reportHostedSection.types';

const cardStyle = { background: "#fff", border: `1px solid ${border}` } as const;

const sliceColor = (i: number) =>
  i === 0 ? CORAL : NAVY;
const sliceOpacity = (i: number, n: number) =>
  i === 0 ? 1 : 0.85 - ((i - 1) / Math.max(1, n - 2)) * 0.5;

type DistributionRow = { label: string; value: number; pct: number };

const DistributionDonut = ({
  title,
  subtitle,
  data,
}: {
  title: string;
  subtitle: string;
  data: DistributionRow[];
}) => {
  const printStatic = useReportsPrintStaticLayout();
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const R = 60;
  const CX = 90;
  const CY = 90;
  const STROKE = 28;
  const C = 2 * Math.PI * R;

  let offsetAcc = 0;
  const segments = data.map((d, i) => {
    const share = total > 0 ? d.value / total : 0;
    const dash = share * C;
    const seg = {
      ...d,
      share,
      norm: Math.round(share * 100),
      dash,
      offset: offsetAcc,
      color: sliceColor(i),
      opacity: sliceOpacity(i, data.length),
    };
    offsetAcc += dash;
    return seg;
  });

  return (
    <div className="rounded-2xl p-6 shadow-sm h-full" style={cardStyle}>
      <h3 className="who-were-reaching__card-title text-base font-semibold mb-1" style={{ color: foreground, fontFamily: "'Lora', serif" }}>
        {title}
      </h3>
      <p className="text-sm mb-5" style={{ color: muted }}>
        {subtitle}
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <div className="relative shrink-0" style={{ width: 180, height: 180 }}>
          <svg viewBox="0 0 180 180" width={180} height={180}>
            <g transform={`rotate(-90 ${CX} ${CY})`}>
              {segments.map((s, i) =>
                printStatic ? (
                  <circle
                    key={s.label}
                    cx={CX}
                    cy={CY}
                    r={R}
                    fill="none"
                    stroke={s.color}
                    strokeOpacity={s.opacity}
                    strokeWidth={STROKE}
                    strokeDasharray={`${s.dash} ${C - s.dash}`}
                    strokeDashoffset={-s.offset}
                  />
                ) : (
                  <motion.circle
                    key={s.label}
                    cx={CX}
                    cy={CY}
                    r={R}
                    fill="none"
                    stroke={s.color}
                    strokeOpacity={s.opacity}
                    strokeWidth={STROKE}
                    strokeDasharray={`${s.dash} ${C - s.dash}`}
                    strokeDashoffset={-s.offset}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                  />
                ),
              )}
            </g>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-semibold tabular-nums" style={{ color: foreground }}>
              {total.toLocaleString()}
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: muted, fontFamily: "monospace" }}>
              total
            </span>
          </div>
        </div>
        <div className="grid w-max max-w-full min-w-0 grid-cols-[12px_auto_auto] items-center gap-x-12 gap-y-2">
          {segments.map((s) => (
            <Fragment key={s.label}>
              <span
                className="w-3 h-3 rounded-full"
                style={{ background: s.color, opacity: s.opacity }}
              />
              <span className="text-sm" style={{ color: foreground }}>
                {s.label}
              </span>
              <div className="flex items-baseline justify-end gap-3">
                <span className="text-sm font-medium tabular-nums" style={{ color: foreground }}>
                  {s.value.toLocaleString()}
                </span>
                <span
                  className="text-[10px] uppercase tracking-[0.2em] tabular-nums w-8 text-right"
                  style={{ color: muted, fontFamily: "monospace" }}
                >
                  {s.norm}%
                </span>
              </div>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const formatCountyLabel = (name: string): string => {
  const stripped = name.replace(/\s*County$/i, "").trim();
  return stripped.toLowerCase().includes("county")
    ? stripped
    : `${stripped} County`;
};

const parseCounties = (data: Record<string, unknown>): CountyEngagementRow[] =>
  coerceWhoWereReachingCounties(data.counties);

/**
 * Who We're Reaching section: county map, sidebar bars, and demographic donuts.
 * @param props - Hosted section snapshot and customer context.
 * @returns Population profile section markup.
 */
const PopulationProfile = ({
  data,
  customer,
  reportingPeriod,
}: ReportHostedSectionProps) => {
  const printStatic = useReportsPrintStaticLayout();
  const displayData = normalizeWhoWereReachingSnapshot(
    data,
    customer,
    reportingPeriod,
  );
  const counties = parseCounties(displayData);
  const engagedTotal =
    typeof displayData.engagedTotal === "number" && Number.isFinite(displayData.engagedTotal)
      ? displayData.engagedTotal
      : counties.reduce((sum, row) => sum + row.memberCount, 0);
  const engagedTotalIsMock =
    readReportFieldSource(displayData, 'engagedTotal') === 'mock';

  const usStateCode =
    typeof displayData.usStateCode === "string"
      ? displayData.usStateCode
      : customer.usStateCode ?? null;

  const { geo, loading: geoLoading } = useStateCountyGeo(usStateCode);

  const ageDist = coerceWhoWereReachingDistribution(displayData.age);
  const raceDist = coerceWhoWereReachingDistribution(displayData.raceEthnicity);

  const max = Math.max(...counties.map((c) => c.memberCount), 1);

  return (
    <section
      id="population"
      data-pdf-section
      className="section--white report-hosted-section-body"
      style={{ background: "#fff", fontFamily: body }}
    >
      <div className="max-w-5xl mx-auto">
        <SectionReveal>
          <div className="who-were-reaching__intro" style={{ marginBottom: "2.5rem" }}>
            <h3
              className="who-were-reaching__page-title"
              style={{
                fontFamily: "var(--reema-font-display)",
                fontWeight: 600,
                fontSize: "var(--reema-text-h2)",
                color: foreground,
                margin: "0 0 0.75rem",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
              }}
            >
              Who we{"\u2019"}re reaching.
            </h3>
            <p
              className="who-were-reaching__page-lede"
              style={{
                fontFamily: body,
                fontSize: "var(--reema-text-body-lg)",
                color: muted,
                margin: 0,
                lineHeight: 1.6,
                opacity: engagedTotalIsMock ? REPORT_MOCK_FIELD_OPACITY : 1,
              }}
            >
              A demographic and clinical-risk snapshot of the {engagedTotal.toLocaleString()} engaged members {"\u2014"} the people behind the funnel.
            </p>
          </div>
        </SectionReveal>

        <SectionReveal delay={0.05}>
          <ReportMockFieldFrame data={displayData} field="counties">
          <div className="rounded-2xl p-6 shadow-sm mb-10 who-were-reaching__card" style={cardStyle}>
            <h3 className="who-were-reaching__card-title text-base font-semibold mb-1" style={{ color: foreground, fontFamily: "'Lora', serif" }}>
              Where members are engaging
            </h3>
            <p className="text-sm mb-5" style={{ color: muted }}>
              Top counties by engaged member count this month {"\u2014"} guides triage outreach capacity by density.
            </p>
            <div className="who-were-reaching-engagement-grid grid md:grid-cols-2 gap-6 items-center">
              <div className="space-y-2 order-2 md:order-1">
                {counties.length === 0 ? (
                  <p className="text-sm" style={{ color: muted }}>
                    No county engagement data for this reporting period yet. Use Refresh data in the builder after the customer name, reporting period, and US state are set.
                  </p>
                ) : null}
                {counties.map((r, i) => {
                  const width = (r.memberCount / max) * 100;
                  const intensity = 0.35 + (r.memberCount / max) * 0.65;
                  const regionLabel = formatCountyLabel(r.name);
                  return (
                    <div key={regionLabel} className="grid grid-cols-[140px_1fr_50px] items-center gap-3">
                      <span className="text-sm truncate" style={{ color: foreground }}>
                        {regionLabel}
                      </span>
                      <div className="h-5 rounded overflow-hidden" style={{ background: secondary }}>
                        {printStatic ? (
                          <div
                            className="h-full rounded"
                            style={{
                              width: `${width}%`,
                              background: CORAL,
                              opacity: intensity,
                            }}
                          />
                        ) : (
                          <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${width}%` }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                            className="h-full rounded"
                            style={{ background: CORAL, opacity: intensity }}
                          />
                        )}
                      </div>
                      <span className="text-sm font-medium tabular-nums text-right" style={{ color: foreground }}>
                        {r.memberCount}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="order-1 md:order-2">
                {geoLoading && (
                  <p className="text-sm" style={{ color: muted }}>
                    Loading map…
                  </p>
                )}
                {!geoLoading && geo ? (
                  <>
                    <CountyEngagementMap geo={geo} counties={counties} />
                    {counties.length === 0 ? (
                      <p className="text-sm mt-3" style={{ color: muted }}>
                        County counts will appear when warehouse data is available for this period.
                      </p>
                    ) : null}
                  </>
                ) : null}
                {!geoLoading && !geo && (
                  <p className="text-sm" style={{ color: muted }}>
                    Map unavailable for this state.
                  </p>
                )}
              </div>
            </div>
          </div>
          </ReportMockFieldFrame>
        </SectionReveal>

        {(ageDist.length > 0 || raceDist.length > 0) && (
          <SectionReveal delay={0.1}>
            <div className="who-were-reaching-donut-grid grid gap-6">
              {ageDist.length > 0 ? (
                <ReportMockFieldFrame data={displayData} field="age">
                  <DistributionDonut title="Age distribution" subtitle="Engaged members by age range." data={ageDist} />
                </ReportMockFieldFrame>
              ) : null}
              {raceDist.length > 0 ? (
                <ReportMockFieldFrame data={displayData} field="raceEthnicity">
                  <DistributionDonut title="Race & ethnicity" subtitle="Self-reported, engaged members." data={raceDist} />
                </ReportMockFieldFrame>
              ) : null}
            </div>
          </SectionReveal>
        )}
      </div>
    </section>
  );
};

export default PopulationProfile;
